import React, { useCallback, useMemo, useState } from 'react';
import { ResourceType } from '@cotera/api';
import { ArtifactSidePanel } from '@cotera/client/v0/chat/artifact-side-panel';
import {
  buildResourceArtifactDescriptor,
  type ArtifactDescriptor,
} from '@cotera/client/v0/chat/artifacts';
import {
  ResourceArtifactPanelContext,
  type OpenResourceArtifactOptions,
  type ResourceArtifactPanelContextValue,
} from './resource-artifact-panel-context';

export {
  useResourceArtifactPanel,
  type OpenResourceArtifactOptions,
  type ResourceArtifactPanelContextValue,
} from './resource-artifact-panel-context';

/**
 * Wraps editor content in a transient artifact side panel. Unlike chat, nothing is
 * persisted — clicking a resource pill shows it beside the editor; closing clears it.
 * Use on the standalone editor pages (agent-builder, document editor) in page mode.
 */
export const ResourceArtifactPanelProvider: React.FC<{
  children: React.ReactNode;
  /** Mobile: icon for the editor (non-artifact) tab. */
  primaryTabIcon?: React.ReactNode;
}> = ({ children, primaryTabIcon }) => {
  const [openArtifact, setOpenArtifact] = useState<ArtifactDescriptor | null>(
    null
  );

  const openResourceArtifact = useCallback(
    (
      resourceType: ResourceType,
      resourceId: string,
      options?: OpenResourceArtifactOptions
    ) => {
      setOpenArtifact(
        buildResourceArtifactDescriptor(
          resourceType,
          resourceId,
          options?.name,
          options?.payload
        )
      );
    },
    []
  );
  const closeArtifact = useCallback(() => setOpenArtifact(null), []);

  const value = useMemo<ResourceArtifactPanelContextValue>(
    () => ({ openResourceArtifact, closeArtifact }),
    [openResourceArtifact, closeArtifact]
  );

  const artifacts = useMemo(
    () => (openArtifact !== null ? [openArtifact] : []),
    [openArtifact]
  );

  return (
    <ResourceArtifactPanelContext.Provider value={value}>
      <ArtifactSidePanel
        artifacts={artifacts}
        primaryTabIcon={primaryTabIcon}
        onRequestClose={closeArtifact}
        artifactCardResizeHandle
      >
        {children}
      </ArtifactSidePanel>
    </ResourceArtifactPanelContext.Provider>
  );
};
